// ============================================================================
// RECOVERABLE EXPENSE SERVICE — ADR-0037 capture. Posts on create.
// ============================================================================

import { get, post, del } from '../client/axiosClient';
import { recoverableExpenseEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  RecoverableExpenseDto, CreateRecoverableExpenseRequest, RecoverableExpenseQueryParams,
  RecoveryTargetDto,
} from '../types';

class RecoverableExpenseService {
  async getAll(params?: RecoverableExpenseQueryParams): Promise<PaginatedResponse<RecoverableExpenseDto>> {
    const endpoint = params ? buildEndpointWithQuery(recoverableExpenseEndpoints.list(), params) : recoverableExpenseEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<RecoverableExpenseDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Entities + BUs the expense can be routed to for recovery (ADR-0038). */
  async getRecoveryTargets(): Promise<RecoveryTargetDto[]> {
    const response = await get<ApiResponse<RecoveryTargetDto[]>>(recoverableExpenseEndpoints.recoveryTargets());
    return Array.from(response.data.data || []);
  }

  async getById(id: ID): Promise<RecoverableExpenseDto> {
    const response = await get<ApiResponse<RecoverableExpenseDto>>(recoverableExpenseEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreateRecoverableExpenseRequest): Promise<RecoverableExpenseDto> {
    const response = await post<ApiResponse<RecoverableExpenseDto>>(recoverableExpenseEndpoints.create(), data);
    return response.data.data;
  }

  async cancel(id: ID, rowVersion: number, reason?: string): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(recoverableExpenseEndpoints.cancel(id), reason ? { rowVersion, reason } : { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }
}

export const recoverableExpenseService = new RecoverableExpenseService();
export default recoverableExpenseService;
